/**
 * global.js — the two site-wide overlays, and the theme's shared helpers.
 * Loaded (deferred) from <head> in layout/theme.liquid on every page, which
 * makes it the first script the theme runs. See the comment on that tag: the
 * placement is what lets every other script consume `window.kosha`.
 *
 * window.kosha — the theme's shared helpers, consumed by product.js and
 * collection.js. See the SHARED HELPERS block for each one:
 *   cartRoute, updateCartCount, seedCartCount, formatMoney, cleanMoneyFormat,
 *   shopMoneyFormat, escapeHtml, prefersReducedMotion
 *
 * Custom elements:
 *
 *   <kosha-cart-drawer>        snippets/cart-drawer.liquid
 *     .openDrawer(trigger?)    open, trap focus, remember where to return it
 *     .closeDrawer()
 *     .refresh()               re-render from the server, returns a Promise
 *     listens for `kosha:cart:added` on document → refresh, then auto-open
 *     emits   `kosha:cart:added` with detail.source === 'cart-drawer' after
 *             its own upsell add, so other listeners see it like any other add
 *
 *   <kosha-predictive-search>  snippets/predictive-search.liquid
 *     .openOverlay(trigger?)
 *     .closeOverlay()
 *
 * No modules, no imports — one IIFE, matching assets/product.js, so the
 * deferred script works everywhere and there is no build step.
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS WORTH KNOWING BEFORE EDITING
 *
 * 1. Cart refreshes are server renders, not client rebuilds. Every mutation
 *    asks `/cart/change.js` (or `/cart/add.js`) for `sections=main-cart` in the
 *    same request, and swaps the returned `[data-cart-inner]` into the drawer.
 *    The reason is the upsell row: it reads the `theme.upsell_list` metafield,
 *    which is not in `/cart.js` and not in `/products/<handle>.js`, so a
 *    client-side rebuild could never cross-sell a product added after page
 *    load. See the doc block in snippets/cart-drawer.liquid.
 *
 * 2. Scroll locking is class-based, not `base.css`'s `html:has(dialog[...])`
 *    rule. Neither overlay is a `<dialog>` (see the snippets for why), so
 *    `html.kosha-scroll-lock` — declared in assets/cart-drawer.css — is
 *    toggled here instead, through a shared counter so the drawer and the
 *    search overlay cannot unlock the page out from under each other.
 *
 * 3. Every cart mutation is guarded by a single `pending` flag on the drawer.
 *    A second tap on + while a request is in flight is dropped, not queued:
 *    one gesture, one `/cart/change.js` request.
 * ---------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* ==========================================================================
     CONSTANTS
     ========================================================================== */

  /**
   * FREE_SHIPPING_THRESHOLD_CENTS — "Free U.S. shipping on orders $100+".
   * The rule itself lives in Shopify Shipping settings and is not readable from
   * Liquid or from the Ajax API, so it is hardcoded in exactly two places: here
   * and in snippets/cart-drawer.liquid, under the same name. The Liquid render
   * seeds the bar; this value keeps it correct after client-side changes. The
   * drawer also publishes it as `data-threshold`, which wins when present, so
   * in practice only the Liquid constant has to move.
   */
  var FREE_SHIPPING_THRESHOLD_CENTS = 10000;

  /* The section file whose render is the drawer's content. See
     sections/main-cart.liquid. */
  var CART_SECTION_ID = 'main-cart';

  var SEARCH_DEBOUNCE_MS = 200;
  var SEARCH_MIN_CHARS = 2;
  var SEARCH_LIMIT = 5;
  var ADDED_HOLD_MS = 2000;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /* ==========================================================================
     SHARED HELPERS  →  window.kosha

     THE ONE COPY. This block existed in several files at once (here,
     product.js, collection.js) with two disagreeing `escapeHtml`s; the section
     scripts now hold one-line delegates.

     LOAD-BEARING: theme.liquid loads global.js from <head> with `defer`, and
     deferred scripts run in document order, so this file always executes
     before the section scripts in <body>. Moving that tag back to the end of
     <body> would invert the order and break every delegate.
     ========================================================================== */

  function cartRoute(path) {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + path;
  }

  /**
   * Rewrites every [data-cart-count] the header rendered.
   *
   * The header prints the raw count including "0", so the element also carries
   * data-empty="true|false" and the zero state is hidden in CSS rather than by
   * emptying the node — an empty badge would still occupy its 17px circle.
   */
  function updateCartCount(count) {
    var value = parseInt(count, 10);
    if (isNaN(value)) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = String(value);
      node.setAttribute('data-empty', value > 0 ? 'false' : 'true');
    });
  }

  function seedCartCount() {
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      var value = parseInt(node.textContent, 10) || 0;
      node.setAttribute('data-empty', value > 0 ? 'false' : 'true');
    });
  }

  /**
   * Shopify money formatter. Only used for values this file computes (the
   * free-shipping remainder, predictive-search prices) — anything Liquid
   * rendered arrives already formatted.
   */
  function formatMoney(cents, format) {
    var value = Number(cents);
    if (!isFinite(value)) return '';
    var template = format || '${{amount}}';
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';

    function group(number, precision, thousands, decimal) {
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var formatted;
    switch (key) {
      case 'amount_no_decimals':
        formatted = group(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = group(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = group(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = group(value, 2, "'", '.');
        break;
      default:
        formatted = group(value, 2, ',', '.');
    }
    return template.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  /* Some shops carry markup inside money_format (`<span class=money>…`).
     Everything here writes through textContent, so the tags are stripped once
     rather than printed. */
  function cleanMoneyFormat(raw) {
    if (!raw) return '${{amount}}';
    var stripped = String(raw).replace(/<[^>]*>/g, '').trim();
    return stripped || '${{amount}}';
  }

  /* Read from the drawer's `data-money-format` (theme.liquid renders the
     drawer in every layout). Not memoised: a cache would be stale the day a
     currency switcher lands. */
  function shopMoneyFormat() {
    var drawer = document.querySelector('[data-cart-drawer]');
    return cleanMoneyFormat(drawer && drawer.getAttribute('data-money-format'));
  }

  /* For code that builds markup as a string (collection.js's swapped cards).
     Unused here; shared, and the former copies disagreed on `'`. Strict
     version wins: `&#39;` parses back to `'`, so nothing rendered changes. */
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Returns a real boolean even with no matchMedia (two of the former copies
     returned `undefined` there, which every caller treated as false anyway). */
  function prefersReducedMotion() {
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Frozen: no section script can monkey-patch a cart helper by accident. */
  window.kosha = Object.freeze({
    cartRoute: cartRoute,
    updateCartCount: updateCartCount,
    seedCartCount: seedCartCount,
    formatMoney: formatMoney,
    cleanMoneyFormat: cleanMoneyFormat,
    shopMoneyFormat: shopMoneyFormat,
    escapeHtml: escapeHtml,
    prefersReducedMotion: prefersReducedMotion,
  });

  /* ==========================================================================
     OVERLAY PLUMBING — scroll lock and focus trap, shared by both overlays.
     ========================================================================== */

  /* One counter for both overlays. Without it, opening the drawer from the
     search overlay and closing the drawer would unlock the page while search
     is still up. */
  var lockDepth = 0;

  function lockScroll() {
    lockDepth += 1;
    if (lockDepth > 1) return;
    /* No scrollbar compensation. It used to set `--kosha-scrollbar-w` here and
       cart-drawer.css turned that into `padding-inline-end` on <html>, which is
       subtracted from the width of every section on the page — so a bad reading
       squeezed the whole site into a column. See the note in cart-drawer.css. */
    document.documentElement.classList.add('kosha-scroll-lock');
  }

  function unlockScroll() {
    if (lockDepth === 0) return;
    lockDepth -= 1;
    if (lockDepth > 0) return;
    document.documentElement.classList.remove('kosha-scroll-lock');
    document.documentElement.style.removeProperty('--kosha-scrollbar-w');
  }

  function focusableWithin(root) {
    if (!root) return [];
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (node) {
        /* offsetParent is null for display:none subtrees; the empty-state and
           the line list swap in and out, so the list has to be recomputed on
           every Tab rather than cached at open time. */
        return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
      }
    );
  }

  /** Cycle Tab / Shift+Tab inside `root`. Returns true when it handled it. */
  function trapTab(event, root) {
    if (event.key !== 'Tab') return false;
    var nodes = focusableWithin(root);
    if (nodes.length === 0) {
      event.preventDefault();
      if (root && typeof root.focus === 'function') root.focus();
      return true;
    }
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    var active = document.activeElement;

    if (!root.contains(active)) {
      event.preventDefault();
      first.focus();
      return true;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return true;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
      return true;
    }
    return false;
  }

  /* --------------------------------------------------------- overlay handoff */

  /* A /cart page reload owed to a cart edit made in the drawer (see
     `closeDrawer`). Held rather than fired on the spot, because closing the
     drawer is not always the end of the interaction: tapping the header search
     icon while the drawer is open closes the drawer *in order to open search*,
     and reloading there threw the search overlay away before it appeared. The
     reload is flushed when the last overlay closes and the shopper is actually
     looking at the stale page again. */
  var cartReloadPending = false;

  /* True only for the duration of a handoff — one overlay closing another so it
     can take its place. Nothing may act on "no overlay is open" while it is
     set, because the incoming overlay has not been marked open yet. */
  var overlayHandoff = false;

  /* Closes whichever overlay is open other than `keep`, so the two can never
     stack. */
  function closeOtherOverlays(keep) {
    var previous = overlayHandoff;
    overlayHandoff = true;
    try {
      document.querySelectorAll('[data-cart-drawer][data-open]').forEach(function (node) {
        if (node !== keep && typeof node.closeDrawer === 'function') node.closeDrawer();
      });
      document.querySelectorAll('[data-search-overlay][data-open]').forEach(function (node) {
        if (node !== keep && typeof node.closeOverlay === 'function') node.closeOverlay();
      });
    } finally {
      overlayHandoff = previous;
    }
  }

  /** Fire the owed /cart reload, but only once the shopper is back on the page. */
  function flushCartReload() {
    if (!cartReloadPending || overlayHandoff) return;
    if (document.querySelector('[data-cart-drawer][data-open], [data-search-overlay][data-open]')) {
      return;
    }
    cartReloadPending = false;
    window.location.reload();
  }

  /* ==========================================================================
     <kosha-cart-drawer>
     ========================================================================== */

  class KoshaCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.panel = this.querySelector('[data-cart-panel]');
      this.content = this.querySelector('[data-cart-content]');
      this.rewards = this.querySelector('[data-cart-rewards]');
      this.rewardsFill = this.querySelector('[data-cart-rewards-fill]');
      this.rewardsTrack = this.querySelector('[data-cart-rewards-track]');
      this.rewardsText = this.querySelector('[data-cart-rewards-text]');
      this.rewardsMarker = this.querySelector('[data-cart-rewards-marker]');
      this.tiers = this.readTiers();
      this.countNode = this.querySelector('[data-cart-drawer-count]');
      this.status = this.querySelector('[data-cart-status]');
      this.error = this.querySelector('[data-cart-error]');

      this.moneyFormat = cleanMoneyFormat(this.getAttribute('data-money-format'));
      this.rootUrl = this.getAttribute('data-root-url') || '/';
      this.threshold =
        parseInt(this.getAttribute('data-threshold'), 10) || FREE_SHIPPING_THRESHOLD_CENTS;

      this.isOpen = false;
      this.pending = false;
      this.dirty = false;
      this.trigger = null;
      this.addedTimer = null;

      this.onKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));

      /* CAPTURE, because `scroll` does not bubble: a listener on this element
         only hears the rail's scroll on the way down to it. Bound here rather
         than on the rail so it survives every re-render — the rail is replaced
         on each cart change, this element never is. */
      var element = this;
      this.addEventListener(
        'scroll',
        function (event) {
          var rail = event.target;
          if (rail && rail.matches && rail.matches('[data-cart-upsell-rail]')) {
            element.syncUpsellDots(rail);
          }
        },
        true
      );
      /* Delegated, because the footer holding the form is replaced wholesale on
         every cart refresh — a listener bound to the form itself would be
         thrown away with it. */
      this.addEventListener('submit', this.handleSubmit.bind(this));
    }

    /* ------------------------------------------------------------ open/close */

    openDrawer(trigger) {
      if (this.isOpen) return;
      closeOtherOverlays(this);
      this.trigger =
        trigger || (document.activeElement !== document.body ? document.activeElement : null);
      this.isOpen = true;
      this.setAttribute('data-open', '');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown, true);

      var target = this.querySelector('[data-cart-close]') || this.panel;
      var element = this;
      window.requestAnimationFrame(function () {
        if (element.isOpen && target) target.focus();
      });
    }

    closeDrawer() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.removeAttribute('data-open');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown, true);

      var trigger = this.trigger;
      this.trigger = null;
      if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
        trigger.focus();
      }

      /* The /cart page renders the same cart a second time, and this drawer
         does not own that markup — wallet buttons and the Redo app's injected
         checkout button both live in it, so it cannot be section-swapped the
         way the drawer body is. If the shopper edited the cart from the drawer
         while standing on /cart, the page behind is stale the moment the drawer
         shuts, so it is reloaded. Nowhere else needs this.

         Owed, not fired: `flushCartReload` holds it back if this close is a
         handoff to the search overlay, and runs it when that overlay closes. */
      if (this.dirty && document.body.classList.contains('template-cart')) {
        this.dirty = false;
        cartReloadPending = true;
      }
      flushCartReload();
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDrawer();
        return;
      }
      trapTab(event, this.panel);
    }

    /* --------------------------------------------------------- interactions */

    handleSubmit(event) {
      var form = event.target && event.target.closest
        ? event.target.closest('[data-cart-discount]')
        : null;
      if (!form) return;
      event.preventDefault();
      this.discountInput = form.querySelector('[data-cart-discount-input]');
      this.discountBtn = form.querySelector('[data-cart-discount-btn]');
      this.applyDiscount(this.discountInput ? this.discountInput.value : '');
    }

    handleClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      if (target.closest('[data-cart-overlay]') || target.closest('[data-cart-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      var step = target.closest('[data-qty-step]');
      if (step) {
        event.preventDefault();
        this.stepLine(step);
        return;
      }

      var remove = target.closest('[data-line-remove]');
      if (remove) {
        /* A real link, so it still works with JavaScript off. */
        event.preventDefault();
        var line = remove.closest('[data-cart-line]');
        if (line) this.changeLine(line.getAttribute('data-line-key'), 0, line);
        return;
      }

      var dot = target.closest('[data-cart-upsell-dot]');
      if (dot) {
        event.preventDefault();
        this.scrollUpsellTo(parseInt(dot.getAttribute('data-cart-upsell-dot'), 10));
        return;
      }

      var add = target.closest('[data-cart-upsell-add]');
      if (add) {
        event.preventDefault();
        this.addUpsell(add);
      }
    }

    /**
     * Bring one upsell card into view. Measured off the first two cards rather
     * than assumed, so the step stays right whatever the card width and gap
     * work out to at this viewport.
     */
    scrollUpsellTo(index) {
      var rail = this.querySelector('[data-cart-upsell-rail]');
      if (!rail || isNaN(index)) return;
      var cards = rail.querySelectorAll('[data-cart-upsell-card]');
      if (!cards[index]) return;
      rail.scrollTo({ left: cards[index].offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
    }

    /**
     * Light the dot for whichever card is in view.
     *
     * Bound in the CAPTURE phase, because `scroll` does not bubble — a listener
     * on the drawer only ever hears a descendant's scroll on the way down. That
     * is also what makes this survive a re-render: the listener lives on the
     * drawer, which is never replaced, rather than on the rail, which is.
     */
    syncUpsellDots(rail) {
      var dots = this.querySelectorAll('[data-cart-upsell-dot]');
      if (!dots.length) return;
      var cards = rail.querySelectorAll('[data-cart-upsell-card]');
      if (!cards.length) return;

      var step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
      var index = step > 0 ? Math.round(rail.scrollLeft / step) : 0;
      index = Math.max(0, Math.min(index, cards.length - 1));

      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.classList.toggle('is-on', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    handleChange(event) {
      var input = event.target;
      if (!input || typeof input.closest !== 'function') return;
      if (!input.matches('[data-qty-input]')) return;
      var line = input.closest('[data-cart-line]');
      if (!line) return;

      var authoritative = parseInt(line.getAttribute('data-line-quantity'), 10);
      if (isNaN(authoritative) || authoritative < 0) authoritative = 1;

      /* A mutation is already in flight, so `changeLine` would drop this one —
         one gesture, one request. The typed number must not be left sitting in
         the field as if it had stuck: put the authoritative quantity back and
         say so, rather than displaying a total the cart does not have. */
      if (this.pending) {
        input.value = String(authoritative);
        this.say('Your cart is still updating. Please set that quantity again.');
        return;
      }

      var next = parseInt(input.value, 10);
      if (isNaN(next) || next < 0) next = authoritative;
      this.changeLine(line.getAttribute('data-line-key'), next, line);
    }

    stepLine(button) {
      var line = button.closest('[data-cart-line]');
      if (!line) return;
      var delta = parseInt(button.getAttribute('data-qty-step'), 10) || 0;
      var current = parseInt(line.getAttribute('data-line-quantity'), 10) || 0;
      var next = current + delta;
      if (next < 0) next = 0;
      if (next === current) return;
      this.changeLine(line.getAttribute('data-line-key'), next, line);
    }

    /* -------------------------------------------------------------- mutation */

    /**
     * The only place the cart is changed. `pending` is the in-flight guard the
     * whole drawer shares: a second tap while a request is out is dropped, so
     * one gesture is always exactly one /cart/change.js request.
     */
    changeLine(key, quantity, line) {
      if (!key || this.pending) return;
      var element = this;
      this.pending = true;
      this.dirty = true;
      this.setAttribute('data-busy', '');
      if (line) {
        line.setAttribute('data-line-busy', '');
        /* Optimistic: the number the shopper asked for shows immediately, and
           is overwritten by the server's answer when it lands. */
        var input = line.querySelector('[data-qty-input]');
        if (input) input.value = String(quantity);
        line.setAttribute('data-line-quantity', String(quantity));
      }

      this.request(cartRoute('cart/change.js'), { id: key, quantity: quantity })
        .then(function (data) {
          element.applyCart(data);
          element.announce(quantity === 0 ? 'Item removed from your cart.' : 'Cart updated.');
        })
        .catch(function (error) {
          element.fail(error);
        })
        .then(function () {
          element.pending = false;
          element.removeAttribute('data-busy');
          if (line && document.contains(line)) line.removeAttribute('data-line-busy');
        });
    }

    /**
     * What a pre-order line must carry, for one variant id, or null.
     *
     * ------------------------------------------------------------------------
     * THE GAP THIS CLOSES
     * ------------------------------------------------------------------------
     * "PRE-ORDER" under a garment's name in the cart, and again on the checkout
     * page, is not a feature either page has: it is an ordinary line item
     * property, written when the line is added, which Shopify then prints in
     * both places on its own. The buy column has always written it
     * (`preorderLine()` in assets/product.js). This drawer never did — its ADD
     * posted a bare `{id, quantity}` — so a pre-order taken from "Complete your
     * set" reached checkout looking like something in stock.
     *
     * THE DATA IS SERVER-RENDERED, NOT ASKED OF THE APP. `[data-cart-preorder]`
     * is built by snippets/cart-drawer.liquid from the same
     * `preorder-entries.liquid` the product page's index is built from, so the
     * two cannot disagree, and it is right in the local preview where the
     * pre-order app's embed never loads at all. It rides inside
     * `[data-cart-inner]`, so it is re-rendered with the cards it describes.
     *
     * EVERY FAILURE HERE IS "ADD IT ANYWAY". No payload, unparseable payload,
     * variant not listed — all return null and the add proceeds exactly as it
     * did before. Losing the note is bad; refusing to sell is worse.
     *
     * The selling plan is attached only when the server said `planRequired`.
     * On this store no campaign needs one today (a pre-order that charges full
     * price and adjusts nothing is, to Shopify, a plain sale with a label on
     * it) — see snippets/preorder-entries.liquid for the three conditions.
     *
     * `_essential_preorder_campaign_id` is deliberately NOT written. It is the
     * app's own hidden property and the theme cannot invent one; the app falls
     * back to matching on the variant id when it is absent, which is the same
     * position the buy column is in on a page the embed has not booted on.
     */
    preorderLine(variantId) {
      var node = this.querySelector('[data-cart-preorder]');
      if (!node) return null;

      var data;
      try {
        data = JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }

      var entry = data && data.variants && data.variants[String(variantId)];
      if (!entry) return null;

      var copy = (data && data.copy) || {};
      var key = copy.noteKey || 'Note';
      var text = copy.noteText || 'PRE-ORDER';

      var properties = {};
      properties[key] = text;

      return {
        properties: properties,
        sellingPlan: entry.planRequired ? entry.plan || null : null,
      };
    }

    addUpsell(button) {
      if (this.pending) return;
      /* THE SIZE COMES FROM THIS CARD, not from the drawer.

         Scoped to the button's own card because the slider holds three of
         them: a document-wide lookup would find the first card's size control
         and add that variant whichever ADD was pressed. `data-variant-id` is
         only the option Liquid preselected, and is the fallback for a product
         with nothing to choose. */
      var card = button.closest('[data-cart-upsell-card]');
      var chosen = card && card.querySelector('[data-cart-upsell-size]');
      var id = parseInt(
        (chosen && chosen.value) || button.getAttribute('data-variant-id'),
        10
      );
      if (!id) return;

      /* A PRE-ORDER HAS TO SAY SO ON THE LINE (owner, 2026-09-07: "please do
         the gap fix"). `preorderLine` returns null for everything else, so an
         ordinary add posts exactly the body it always did. */
      var item = { id: id, quantity: 1 };
      var preorder = this.preorderLine(id);
      if (preorder) {
        item.properties = preorder.properties;
        if (preorder.sellingPlan) item.selling_plan = preorder.sellingPlan;
      }

      var element = this;
      this.pending = true;
      this.dirty = true;
      this.setAttribute('data-busy', '');
      button.setAttribute('data-state', 'loading');

      this.request(cartRoute('cart/add.js'), { items: [item] })
        .then(function (data) {
          var item = data && data.items ? data.items[0] : null;
          element.applyCart(data);
          element.announce('Added to your cart.');
          element.dispatchEvent(
            new CustomEvent('kosha:cart:added', {
              bubbles: true,
              composed: true,
              detail: { item: item, cart: null, source: 'cart-drawer' },
            })
          );
        })
        .catch(function (error) {
          element.fail(error);
          if (document.contains(button)) button.removeAttribute('data-state');
        })
        .then(function () {
          element.pending = false;
          element.removeAttribute('data-busy');
        });
    }

    /**
     * POST helper. Every mutation asks for the drawer's own section in the same
     * request, so a change costs one round trip rather than two.
     */
    request(url, body) {
      body.sections = CART_SECTION_ID;
      body.sections_url = this.rootUrl;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      });
    }

    /** Re-render from the server with no mutation. Used by `kosha:cart:added`. */
    /**
     * Apply a discount code.
     *
     * Shopify has no AJAX endpoint for this — the Cart API cannot set a code,
     * and checkout is normally the first place a shopper can type one. What
     * does work is requesting `/discount/<code>`, which puts the code on the
     * cart session; Liquid then reports it through
     * `cart.cart_level_discount_applications`, which the footer already renders.
     *
     * A code Shopify rejects is accepted silently and simply never appears,
     * which reads as the button doing nothing — so `/cart.js` is checked
     * afterwards and the shopper is told either way.
     */
    applyDiscount(code) {
      var element = this;
      var clean = String(code || '').trim();
      if (!clean) return;

      this.setDiscountMessage('');
      if (this.discountBtn) this.discountBtn.disabled = true;

      fetch(this.rootUrl + 'discount/' + encodeURIComponent(clean), { redirect: 'follow' })
        .then(function () {
          return element.refresh();
        })
        .then(function () {
          return fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } }).then(
            function (response) {
              return response.json();
            }
          );
        })
        .then(function (cart) {
          var hit = null;
          ((cart && cart.discount_codes) || []).forEach(function (entry) {
            if (entry && String(entry.code).toUpperCase() === clean.toUpperCase()) hit = entry;
          });
          if (hit && hit.applicable !== false) {
            element.setDiscountMessage('');
            var input = element.querySelector('[data-cart-discount-input]');
            if (input) input.value = '';
            element.announce('Discount applied.');
          } else {
            element.setDiscountMessage("That code isn't valid for this cart.");
          }
        })
        .catch(function () {
          element.setDiscountMessage('Sorry, we could not apply that code.');
        })
        .then(function () {
          var btn = element.querySelector('[data-cart-discount-btn]');
          if (btn) btn.disabled = false;
        });
    }

    setDiscountMessage(text) {
      var node = this.querySelector('[data-cart-discount-msg]');
      if (node) node.textContent = text || '';
      /* A rejected code must not leave the shopper looking at a folded-away
         link with her message hidden inside it. The <details> closes itself on
         the next re-render, which is right after a code applies and wrong after
         one fails, so failure re-opens it. */
      if (!text) return;
      var wrap = this.querySelector('[data-cart-discount-wrap]');
      if (wrap) wrap.open = true;
    }

    refresh() {
      var element = this;
      var url = this.rootUrl + '?sections=' + CART_SECTION_ID;
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          element.render(data && data[CART_SECTION_ID]);
          element.sync();
        })
        .catch(function () {
          /* A failed refresh must never block the drawer from opening — the
             shopper still sees the cart as it was, which is recoverable. */
          element.sync();
        });
    }

    /* --------------------------------------------------------------- render */

    /**
     * THE DRAWER MUST NEVER BE LEFT SHOWING A CART THAT NO LONGER EXISTS
     * (owner, 2026-09-05: "when i try to click remove on an item it just
     * removes the top progress bar and doesn't let me remove the leggings").
     *
     * What she photographed is this method half-succeeding. `sync()` reads the
     * count and the totals straight off the JSON response, so the badge went to
     * 0 and the reward bar disappeared. `render()` reads the drawer's markup out
     * of `data.sections`, and when that is missing it returns false and simply
     * does nothing — so the line item, its quantity and the subtotal all stayed
     * exactly as they were. Removed everywhere except the one place she was
     * looking.
     *
     * The mutation itself is fine: POSTing the same change.js request the
     * drawer sends empties the cart correctly, so this is only ever about the
     * re-render. The section can go missing for reasons this theme does not
     * control — a preview session answering from a different theme, a section
     * render that times out, an app that 500s inside it.
     *
     * So a failed render is now treated as a failed render rather than as
     * nothing: it falls back to fetching the drawer fresh. One extra request,
     * only on a path that is currently broken, and the shopper can never be
     * left looking at a stale cart.
     */
    applyCart(data) {
      var html = data && data.sections && data.sections[CART_SECTION_ID];
      if (!this.render(html)) this.refresh();
      this.sync(data);
    }

    /**
     * Swap `[data-cart-inner]` out of a section render. Scroll position and
     * keyboard focus are both restored across the swap — a stepper that moves
     * focus to nowhere on every tap is unusable with a keyboard.
     */
    render(html) {
      if (!html || !this.content) return false;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var next = parsed.querySelector('[data-cart-inner]');
      if (!next) return false;

      var scroll = this.content.querySelector('[data-cart-scroll]');
      var scrollTop = scroll ? scroll.scrollTop : 0;
      var focusHint = this.captureFocus();

      this.content.replaceChildren(next);

      var nextScroll = this.content.querySelector('[data-cart-scroll]');
      if (nextScroll) nextScroll.scrollTop = scrollTop;
      this.restoreFocus(focusHint);
      return true;
    }

    captureFocus() {
      var active = document.activeElement;
      if (!active || !this.content || !this.content.contains(active)) return null;
      var line = active.closest('[data-cart-line]');
      var role = null;
      if (active.matches('[data-qty-step]')) role = 'step' + active.getAttribute('data-qty-step');
      else if (active.matches('[data-qty-input]')) role = 'qty';
      else if (active.matches('[data-line-remove]')) role = 'remove';
      else if (active.closest('[data-cart-upsell-add]')) role = 'upsell';
      return { key: line ? line.getAttribute('data-line-key') : null, role: role };
    }

    restoreFocus(hint) {
      if (!hint || !hint.role) return;
      var selector;
      if (hint.role === 'upsell') {
        selector = '[data-cart-upsell-add]';
        /* Any ADD will do: the one that was pressed has usually just become a
           cart line and taken its card with it. */
      } else if (hint.key) {
        var scope = '[data-line-key="' + CSS.escape(hint.key) + '"] ';
        if (hint.role === 'qty') selector = scope + '[data-qty-input]';
        else if (hint.role === 'remove') selector = scope + '[data-line-remove]';
        else selector = scope + '[data-qty-step="' + hint.role.slice(4) + '"]';
      }
      var target = selector ? this.content.querySelector(selector) : null;
      /* The line (or the upsell) can legitimately be gone — a remove, or a
         cross-sell that just became a cart line. Fall back to the close button
         so focus never lands on <body>. */
      if (!target) target = this.querySelector('[data-cart-close]');
      if (target) target.focus();
    }

    /**
     * Bring the shell (count badge, free-shipping bar) back in step with the
     * content. Totals come from the response when there is one, and otherwise
     * straight off `[data-cart-inner]`'s data attributes — which is why a
     * `/cart/add.js` response, which carries no cart totals at all, needs no
     * extra request.
     */
    sync(data) {
      var inner = this.content ? this.content.querySelector('[data-cart-inner]') : null;
      var count = data && typeof data.item_count === 'number' ? data.item_count : null;
      var total = data && typeof data.total_price === 'number' ? data.total_price : null;

      if (inner) {
        if (count === null) count = parseInt(inner.getAttribute('data-count'), 10);
        if (total === null) total = parseInt(inner.getAttribute('data-total'), 10);
      }
      if (isNaN(count) || count === null) return;
      if (isNaN(total) || total === null) total = 0;

      updateCartCount(count);
      if (this.countNode) this.countNode.textContent = String(count);
      this.updateShipping(total, count);
    }

    /**
     * The reward ladder, read from the same `data-tiers` string Liquid built it
     * from, so the server render and every update share one source and cannot
     * drift. Shape: `cents|label,cents|label`, already in order.
     */
    readTiers() {
      var raw = this.rewards && this.rewards.getAttribute('data-tiers');
      if (!raw) return [];
      return raw
        .split(',')
        .map(function (entry) {
          var parts = entry.split('|');
          var cents = parseInt(parts[0], 10);
          if (!(cents > 0) || !parts[1]) return null;
          return { cents: cents, label: parts[1] };
        })
        .filter(Boolean);
    }

    updateShipping(total, count) {
      if (!this.rewards) return;
      if (count > 0) this.rewards.removeAttribute('data-hidden');
      else this.rewards.setAttribute('data-hidden', 'true');
      if (this.tiers.length === 0) return;

      var reached = 0;
      var next = null;
      this.tiers.forEach(function (tier) {
        if (total >= tier.cents) reached += 1;
        else if (!next) next = tier;
      });

      /* EVEN SEGMENTS, the same arithmetic as the Liquid that first paints
         this bar. Each tier owns an equal share of the track, plus however far
         she is across the one she is in — so the divider between two segments
         is exactly where that tier's caption sits underneath. A straight
         percentage of the top tier put the $100 mark at 33% while its caption
         sat at 33% of a three-column grid, and the two never agreed. */
      var count = this.tiers.length;
      var floor = reached > 0 ? this.tiers[reached - 1].cents : 0;
      var percent;
      if (reached >= count) {
        percent = 100;
      } else {
        var span = this.tiers[reached].cents - floor;
        var part = span > 0 ? (total - floor) / span : 0;
        percent = ((reached + part) / count) * 100;
      }
      percent = Math.max(0, Math.min(100, Math.round(percent)));
      if (this.rewardsFill) this.rewardsFill.style.width = percent + '%';
      if (this.rewardsTrack) this.rewardsTrack.setAttribute('aria-valuenow', String(percent));

      /* The rider moves to her position and picks up the icon for whatever she
         is heading towards next. `reached` is the number of rungs already
         cleared, so it indexes the next one; past the last rung it lands on
         the celebration at the end of the list. The icons come off the element
         rather than being written here, so Liquid stays the one place they are
         named and the two can never fall out of step. */
      if (this.rewardsMarker) {
        this.rewardsMarker.style.setProperty('--pct', percent + '%');
        var icons = (this.rewardsMarker.getAttribute('data-icons') || '').split(',');
        var icon = icons[Math.min(reached, icons.length - 1)];
        if (icon && this.rewardsMarker.textContent !== icon) {
          this.rewardsMarker.textContent = icon;
        }
      }
      this.rewards.setAttribute('data-reached', String(reached));

      /* One rung lit per tier cleared, read off `reached` rather than
         recomputed per node, so the classes and the attribute cannot disagree. */
      var rungs = this.rewards.querySelectorAll('[data-cart-rewards-rung]');
      Array.prototype.forEach.call(rungs, function (rung, i) {
        rung.classList.toggle('is-hit', i < reached);
      });

      /* Written only when the sentence actually changes: it sits in a polite
         live region, and an unconditional write re-announces "You're $38.00
         away" on every cart sync. */
      if (!this.rewardsText) return;

      /* Nothing to say once every rung is cleared: the full bar, three lit
         rungs and the celebration on the marker have all said it already.
         Hidden rather than emptied, so the block closes up instead of holding
         open a blank row — and `hidden` also takes it out of the live region,
         which is what stops a screen reader announcing an empty string. */
      if (!next) {
        this.rewardsText.hidden = true;
        this.rewardsText.textContent = '';
        return;
      }

      this.rewardsText.hidden = false;
      var sentence =
        'You\u2019re ' +
        formatMoney(Math.max(0, next.cents - total), this.moneyFormat) +
        ' away from ' +
        next.label;
      if (this.rewardsText.textContent.trim() !== sentence) {
        this.rewardsText.textContent = sentence;
      }
    }

    /* ---------------------------------------------------------------- status */

    /** Speak into the polite live region. Leaves the error banner alone. */
    say(message) {
      if (!this.status) return;
      this.status.textContent = message;
      var element = this;
      if (this.addedTimer) clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(function () {
        if (element.status) element.status.textContent = '';
      }, ADDED_HOLD_MS);
    }

    /** A successful outcome: speak it and clear any banner it supersedes. */
    announce(message) {
      this.setError('');
      this.say(message);
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      if (message) this.error.removeAttribute('hidden');
      else this.error.setAttribute('hidden', '');
    }

    /**
     * Shopify's own message is preferred over a generic one: "Only 1 item was
     * added to your cart due to availability" tells the shopper why the number
     * they asked for did not stick, which "something went wrong" does not.
     *
     * ONLY `description` is trusted. It is the shopper-facing copy on a Cart
     * Ajax 422 and nothing else sets it. `error.message` was tried and removed:
     * a rejected `fetch` is a real `Error`, so `message` is *always* populated
     * — with "Failed to fetch" / "NetworkError when attempting to fetch
     * resource", which is the browser talking to a developer. It made the
     * friendly fallback below unreachable on the one path that needs it most.
     * (A 422 body also carries `message`, but it is the useless "Cart Error".)
     */
    fail(error) {
      var described = error && typeof error.description === 'string' ? error.description.trim() : '';
      var message = described || 'Sorry, we could not update your cart.';
      if (this.status) this.status.textContent = message;
      this.setError(message);
      /* Reconcile: whatever the optimistic UI guessed, the server is right. */
      this.refresh();
    }
  }

  if (!customElements.get('kosha-cart-drawer')) {
    customElements.define('kosha-cart-drawer', KoshaCartDrawer);
  }

  /* ==========================================================================
     <kosha-predictive-search>
     ========================================================================== */

  class KoshaPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.panel = this.querySelector('[data-search-panel]');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.templates = this.querySelector('[data-search-templates]');

      this.suggestUrl = this.getAttribute('data-suggest-url') || '/search/suggest';
      this.searchUrl = this.getAttribute('data-search-url') || '/search';
      this.moneyFormat = shopMoneyFormat();

      this.isOpen = false;
      this.timer = null;
      this.controller = null;
      this.lastQuery = '';
      this.trigger = null;

      this.onKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('input', this.handleInput.bind(this));
    }

    openOverlay(trigger) {
      if (this.isOpen) return;
      closeOtherOverlays(this);
      this.trigger =
        trigger || (document.activeElement !== document.body ? document.activeElement : null);
      this.isOpen = true;
      this.setAttribute('data-open', '');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown, true);

      var element = this;
      window.requestAnimationFrame(function () {
        if (element.isOpen && element.input) element.input.focus();
      });
    }

    closeOverlay() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.removeAttribute('data-open');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown, true);
      if (this.timer) clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.controller = null;

      var trigger = this.trigger;
      this.trigger = null;
      if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
        trigger.focus();
      }

      /* This overlay may have been opened over a /cart page that the drawer
         left stale. Nothing is covering it now, so settle the debt. */
      flushCartReload();
    }

    handleClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (target.closest('[data-search-scrim]') || target.closest('[data-search-close]')) {
        event.preventDefault();
        this.closeOverlay();
      }
    }

    handleInput(event) {
      if (!event.target.matches('[data-search-input]')) return;
      var element = this;
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        element.run();
      }, SEARCH_DEBOUNCE_MS);
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeOverlay();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        var items = Array.prototype.slice.call(this.querySelectorAll('[data-search-result]'));
        if (items.length === 0) return;
        event.preventDefault();

        var index = items.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          index = index < 0 ? 0 : index + 1;
          if (index >= items.length) index = 0;
        } else {
          /* Up from the first result returns to the input, which is where a
             shopper expects to land when they are correcting a typo. */
          if (index <= 0) {
            if (this.input) this.input.focus();
            return;
          }
          index -= 1;
        }
        items[index].focus();
        return;
      }

      trapTab(event, this.panel);
    }

    run() {
      var query = this.input ? this.input.value.trim() : '';
      if (query.length < SEARCH_MIN_CHARS) {
        this.clear();
        return;
      }
      if (query === this.lastQuery) return;
      this.lastQuery = query;

      if (this.controller) this.controller.abort();
      this.controller = typeof AbortController === 'function' ? new AbortController() : null;

      var element = this;
      var url =
        this.suggestUrl +
        '?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=' +
        SEARCH_LIMIT +
        '&resources[options][unavailable_products]=last';

      this.setAttribute('data-loading', '');

      fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.controller ? this.controller.signal : undefined,
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          element.removeAttribute('data-loading');
          var results = (data && data.resources && data.resources.results) || {};
          element.paint(results, query);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          element.removeAttribute('data-loading');
          element.clear();
        });
    }

    clear() {
      this.lastQuery = '';
      if (this.results) this.results.replaceChildren();
      if (this.input) this.input.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }

    cloneRow(index) {
      var source = this.templates.content.children[index];
      return source ? source.cloneNode(true) : null;
    }

    heading(text) {
      var node = this.cloneRow(2);
      if (!node) return null;
      node.querySelector('.psearch__group-label').textContent = text;
      return node;
    }

    paint(results, query) {
      if (!this.results) return;
      var products = results.products || [];
      var collections = results.collections || [];
      var queries = results.queries || [];
      var element = this;
      var list = document.createElement('ul');
      list.className = 'psearch__list';
      list.setAttribute('role', 'list');

      function push(node) {
        if (node) list.appendChild(node);
      }

      if (products.length) {
        push(element.heading('Products'));
        products.forEach(function (product) {
          var row = element.cloneRow(0);
          if (!row) return;
          var link = row.querySelector('[data-search-result]');
          var image = row.querySelector('.psearch__img');
          link.href = product.url;
          row.querySelector('.psearch__title').textContent = product.title;
          row.querySelector('.psearch__price').textContent = formatMoney(
            Math.round(parseFloat(product.price) * 100),
            element.moneyFormat
          );

          var source = product.featured_image && product.featured_image.url;
          if (source) {
            var join = source.indexOf('?') === -1 ? '?' : '&';
            image.src = source + join + 'width=96&height=120&crop=center';
            image.srcset =
              source + join + 'width=96&height=120&crop=center 1x, ' +
              source + join + 'width=192&height=240&crop=center 2x';
          } else {
            image.remove();
          }
          push(row);
        });
      }

      if (collections.length) {
        push(element.heading('Collections'));
        collections.forEach(function (collection) {
          var row = element.cloneRow(1);
          if (!row) return;
          row.querySelector('[data-search-result]').href = collection.url;
          row.querySelector('.psearch__title').textContent = collection.title;
          push(row);
        });
      }

      if (!products.length && !collections.length && queries.length) {
        push(element.heading('Suggestions'));
        queries.forEach(function (suggestion) {
          var row = element.cloneRow(1);
          if (!row) return;
          row.querySelector('[data-search-result]').href = suggestion.url;
          row.querySelector('.psearch__title').textContent = suggestion.text;
          push(row);
        });
      }

      var total = products.length + collections.length;

      if (total === 0 && queries.length === 0) {
        var empty = document.createElement('p');
        empty.className = 'psearch__empty';
        empty.textContent = 'No matches for “' + query + '”. Try “leggings” or “set”.';
        this.results.replaceChildren(empty);
      } else {
        var all = document.createElement('a');
        all.className = 'psearch__all';
        all.href = this.searchUrl + '?q=' + encodeURIComponent(query);
        all.setAttribute('data-search-result', '');
        all.textContent = 'See all results for “' + query + '” →';
        this.results.replaceChildren(list, all);
      }

      if (this.input) this.input.setAttribute('aria-expanded', total > 0 ? 'true' : 'false');
      if (this.status) {
        this.status.textContent = total
          ? total + ' result' + (total === 1 ? '' : 's') + ' for ' + query
          : 'No results for ' + query;
      }
    }
  }

  if (!customElements.get('kosha-predictive-search')) {
    customElements.define('kosha-predictive-search', KoshaPredictiveSearch);
  }

  /* ==========================================================================
     WIRING
     ========================================================================== */

  function drawerElement() {
    return document.querySelector('[data-cart-drawer]');
  }

  function searchElement() {
    return document.querySelector('[data-search-overlay]');
  }

  /* Both triggers stay ordinary links (`/cart`, `/search`), so the header keeps
     working with JavaScript off and modifier-clicks still open a real tab. */
  function isPlainClick(event) {
    return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function' || !isPlainClick(event)) return;

    var cartTrigger = target.closest('[data-cart-drawer-open]');
    if (cartTrigger) {
      var drawer = drawerElement();
      if (drawer && typeof drawer.openDrawer === 'function') {
        event.preventDefault();
        drawer.openDrawer(cartTrigger);
      }
      return;
    }

    var searchTrigger = target.closest('[data-predictive-search-open]');
    if (searchTrigger) {
      var search = searchElement();
      if (search && typeof search.openOverlay === 'function') {
        event.preventDefault();
        search.openOverlay(searchTrigger);
      }
    }
  });

  /**
   * Any add-to-cart anywhere on the page — the PDP form, the "Complete the set"
   * module, and this drawer's own upsell — announces itself with this event.
   * The drawer refreshes first and opens second, so it never flashes the old
   * cart; the ~150ms that costs is hidden behind the button's own "Added"
   * state.
   */
  document.addEventListener('kosha:cart:added', function (event) {
    var drawer = drawerElement();
    if (!drawer || typeof drawer.refresh !== 'function') return;

    var detail = event.detail || {};
    if (detail.cart && typeof detail.cart.item_count === 'number') {
      updateCartCount(detail.cart.item_count);
    }

    /* The drawer's own upsell add already re-rendered from its response and is
       already open. */
    if (detail.source === 'cart-drawer') return;

    drawer.refresh().then(function () {
      drawer.openDrawer(null);
    });
  });

  seedCartCount();
})();
